/**
 * Campaign Frame Pattern Registry
 *
 * Map-based registry that maps pattern type strings to React components.
 * Replaces the hardcoded switch statement in CampaignFrameRenderer.
 *
 * Official SRD patterns are registered at app startup via registerOfficialPatterns().
 * Homebrew patterns can be registered dynamically at runtime.
 *
 * @see registerOfficialPatterns.js
 * @see CampaignFrameRenderer.js
 */

const PATTERN_REGISTRY = new Map();

/**
 * Register a pattern type → component mapping.
 * @param {string} patternType - The pattern type string (e.g., 'ResourceHarvest')
 * @param {React.ComponentType} Component - The React component to render
 */
export const registerPattern = (patternType, Component) => {
  PATTERN_REGISTRY.set(patternType, Component);
};

/**
 * Batch register multiple pattern types.
 * @param {Array<[string, React.ComponentType]>} entries
 */
export const registerPatterns = (entries) => {
  entries.forEach(([type, Component]) => registerPattern(type, Component));
};

/**
 * Look up the component for a pattern type.
 * @param {string} patternType
 * @returns {React.ComponentType|null}
 */
export const getPatternComponent = (patternType) => {
  return PATTERN_REGISTRY.get(patternType) || null;
};

/**
 * List all registered pattern type strings.
 * Useful for validation and LLM schema generation.
 * @returns {string[]}
 */
export const getRegisteredPatternTypes = () => [...PATTERN_REGISTRY.keys()];
